import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';

const TrackOrderPT = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const resultsRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-search when URL has code parameter
  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode && urlCode.trim()) {
      setCode(urlCode.trim());
      setLoading(true); setError(''); setTrackingInfo(null); setSearched(true);
      api.get(`/tracking/public/${urlCode.trim()}`)
        .then(res => {
          setTrackingInfo(res.data);
          setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
        })
        .catch(err => {
          setError(err.response?.status === 404 ? 'Código de rastreio não encontrado.' : 'Erro ao buscar rastreamento. Tente novamente.');
        })
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!code.trim()) { setError('Digite seu código de rastreio'); return; }
    setLoading(true); setError(''); setTrackingInfo(null); setSearched(true);
    try {
      const response = await api.get(`/tracking/public/${code.trim()}`);
      setTrackingInfo(response.data);
      setSearchParams({ code: code.trim() });
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Código de rastreio não encontrado.' : 'Erro ao buscar rastreamento. Tente novamente.');
    } finally { setLoading(false); }
  };

  const scrollTo = (id) => {
    setMobileMenu(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .tp *, .tp *::before, .tp *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .tp {
          font-family: 'Lexend', sans-serif; color: #555; background: #fff;
          overflow-x: hidden; -webkit-font-smoothing: antialiased;
        }
        .tp a { text-decoration: none; }

        /* ===== ANIMATIONS ===== */
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideInLeft { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideInRight { from { opacity: 0; transform: translateX(40px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .tp-fade-up { animation: fadeInUp 0.7s ease forwards; }
        .tp-fade-in { animation: fadeIn 0.6s ease forwards; }

        /* ===== HEADER ===== */
        .tp-header {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1024;
          height: 75px; display: flex; align-items: center;
          padding: 0 5%; justify-content: space-between;
          transition: all 0.4s cubic-bezier(0.4,0,0.2,1);
          background: ${scrolled ? 'rgba(10,10,20,0.95)' : 'rgba(0,0,0,0.29)'};
          ${scrolled ? 'box-shadow: 0 4px 30px rgba(0,0,0,0.25); backdrop-filter: blur(12px);' : ''}
        }
        .tp-header-logo img { height: 45px; transition: transform 0.3s; }
        .tp-header-logo img:hover { transform: scale(1.05); }
        .tp-nav { list-style: none; display: flex; gap: 28px; }
        .tp-nav li a {
          color: #f90; font-size: 16px; font-weight: 500;
          letter-spacing: 0.5px; transition: all 0.3s; cursor: pointer;
          position: relative; padding-bottom: 4px;
        }
        .tp-nav li a::after {
          content: ''; position: absolute; bottom: 0; left: 0;
          width: 0; height: 2px; background: #f90;
          transition: width 0.3s ease;
        }
        .tp-nav li a:hover { color: #fff; }
        .tp-nav li a:hover::after { width: 100%; }

        /* ===== HERO ===== */
        .tp-hero {
          position: relative;
          background: url('/images/ingles.png') center/cover no-repeat;
          min-height: 650px; display: flex; align-items: center;
          padding: 0 5%;
        }
        .tp-hero::before {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 50%, rgba(255,153,0,0.15) 100%);
        }
        .tp-hero-content { position: relative; z-index: 2; animation: fadeInUp 1s ease; }
        .tp-hero h1 {
          font-size: 48px; font-weight: 700; color: #fff;
          line-height: 1.15; max-width: 680px; text-transform: capitalize;
          text-shadow: 0 2px 20px rgba(0,0,0,0.3);
        }
        .tp-hero .tp-sub {
          font-size: 18px; color: rgba(255,255,255,0.9);
          margin-top: 20px; max-width: 550px; line-height: 1.6;
          text-shadow: 0 1px 8px rgba(0,0,0,0.2);
        }
        .tp-hero-badges {
          display: flex; gap: 25px; margin-top: 35px; flex-wrap: wrap;
        }
        .tp-badge {
          display: flex; align-items: center; gap: 10px;
          background: rgba(255,255,255,0.12); backdrop-filter: blur(8px);
          padding: 10px 20px; border-radius: 50px; border: 1px solid rgba(255,255,255,0.15);
        }
        .tp-badge i { color: #f90; font-size: 16px; }
        .tp-badge span { color: #fff; font-size: 13px; font-weight: 500; }

        /* ===== SEARCH BAR ===== */
        .tp-search {
          background: linear-gradient(135deg, #f90 0%, #e08800 100%);
          padding: 28px 5%; display: flex; align-items: center; gap: 18px; flex-wrap: wrap;
          box-shadow: 0 4px 20px rgba(255,153,0,0.3);
          position: relative; z-index: 5;
        }
        .tp-search h4 { color: #fff; font-size: 18px; font-weight: 600; white-space: nowrap; text-shadow: 0 1px 3px rgba(0,0,0,0.15); }
        .tp-search form { display: flex; flex: 1; gap: 12px; }
        .tp-search input {
          flex: 1; min-width: 250px; padding: 16px 22px;
          border: 2px solid transparent; border-radius: 8px; font-size: 16px;
          font-family: 'Lexend', sans-serif; outline: none;
          text-transform: uppercase; transition: all 0.3s;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
        }
        .tp-search input:focus { border-color: #fff; box-shadow: 0 0 0 4px rgba(255,255,255,0.2); }
        .tp-search button {
          padding: 16px 32px; background: rgba(0,0,0,0.25); color: #fff;
          border: 2px solid rgba(255,255,255,0.2); border-radius: 8px; cursor: pointer;
          font-weight: 600; font-size: 16px; font-family: 'Lexend', sans-serif;
          display: flex; align-items: center; gap: 10px;
          transition: all 0.3s; white-space: nowrap;
        }
        .tp-search button:hover { background: rgba(0,0,0,0.4); border-color: rgba(255,255,255,0.4); transform: translateY(-2px); }
        .tp-search button:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
        .tp-spinner { width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 0.6s linear infinite; }

        /* ===== TRACKING RESULTS ===== */
        .tp-results {
          max-width: 960px; margin: 0 auto; padding: 45px 20px 50px;
          animation: fadeInUp 0.6s ease;
        }

        /* Status Header */
        .tp-status-header {
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 16px; padding: 32px; margin-bottom: 24px;
          position: relative; overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          border: 1px solid rgba(255,255,255,0.05);
        }
        .tp-status-header::before {
          content: ''; position: absolute; top: -30%; right: -10%;
          width: 250px; height: 250px; border-radius: 50%;
          background: radial-gradient(circle, rgba(255,153,0,0.08), transparent 70%);
        }
        .tp-status-header::after {
          content: ''; position: absolute; bottom: -30%; left: -10%;
          width: 200px; height: 200px; border-radius: 50%;
          background: radial-gradient(circle, rgba(59,130,246,0.08), transparent 70%);
        }
        .tp-status-top {
          display: flex; justify-content: space-between; align-items: flex-start;
          margin-bottom: 24px; position: relative; z-index: 1; flex-wrap: wrap; gap: 16px;
        }
        .tp-status-code {
          font-size: 11px; color: #94a3b8; font-weight: 600;
          text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px;
        }
        .tp-status-code-val {
          font-size: 28px; font-weight: 700; color: #fff;
          letter-spacing: 3px; font-family: 'Courier New', monospace;
          line-height: 1.2;
        }
        .tp-status-badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 600;
          background: rgba(255,153,0,0.12); color: #f90; border: 1px solid rgba(255,153,0,0.2);
          backdrop-filter: blur(8px);
        }
        .tp-status-badge i { font-size: 12px; }
        .tp-status-badge.delivered { background: rgba(34,197,94,0.12); color: #22c55e; border-color: rgba(34,197,94,0.2); }

        /* Progress Bar */
        .tp-progress { position: relative; z-index: 1; }
        .tp-progress-bar {
          width: 100%; height: 8px; background: rgba(255,255,255,0.08);
          border-radius: 6px; overflow: hidden; margin-bottom: 14px;
        }
        .tp-progress-fill {
          height: 100%; border-radius: 6px;
          background: linear-gradient(90deg, #f90, #ffaa00, #ffcc00);
          transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 0 20px rgba(255,153,0,0.3);
        }
        .tp-progress-labels {
          display: flex; justify-content: space-between;
          font-size: 11px; color: #94a3b8; font-weight: 500;
          letter-spacing: 0.3px;
        }

        /* Route Info Cards */
        .tp-route-cards {
          display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 28px;
        }
        .tp-route-card {
          background: #fff; padding: 20px; border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04); border: 1px solid rgba(0,0,0,0.06);
          display: flex; align-items: center; gap: 14px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .tp-route-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.08); border-color: rgba(255,153,0,0.1); }
        .tp-route-icon {
          width: 48px; height: 48px; border-radius: 10px; display: flex;
          align-items: center; justify-content: center; flex-shrink: 0;
        }
        .tp-route-icon.origin { background: linear-gradient(135deg, #eff6ff, #dbeafe); }
        .tp-route-icon.origin i { color: #3b82f6; font-size: 18px; }
        .tp-route-icon.dest { background: linear-gradient(135deg, #fff7ed, #ffedd5); }
        .tp-route-icon.dest i { color: #f97316; font-size: 18px; }
        .tp-route-icon.date { background: linear-gradient(135deg, #f0fdf4, #dcfce7); }
        .tp-route-icon.date i { color: #22c55e; font-size: 18px; }
        .tp-route-icon.status { background: linear-gradient(135deg, #faf5ff, #f3e8ff); }
        .tp-route-icon.status i { color: #a855f7; font-size: 18px; }
        .tp-route-label { font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .tp-route-value { font-size: 14px; color: #1e293b; font-weight: 600; margin-top: 4px; line-height: 1.4; }

        /* Steps Counter */
        .tp-steps-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 24px; flex-wrap: wrap; gap: 12px;
        }
        .tp-steps-title {
          font-size: 18px; font-weight: 700; color: #1e293b;
          display: flex; align-items: center; gap: 10px;
        }
        .tp-steps-title i { color: #f97316; font-size: 16px; }
        .tp-steps-count {
          font-size: 12px; color: #fff; background: linear-gradient(135deg, #f97316, #ea580c);
          padding: 6px 16px; border-radius: 20px; font-weight: 600;
          box-shadow: 0 2px 8px rgba(249,115,22,0.3);
        }

        /* Timeline */
        .tp-timeline { position: relative; padding-left: 36px; }
        .tp-timeline::before {
          content: ''; position: absolute; left: 12px; top: 8px; bottom: 8px;
          width: 2px; background: linear-gradient(to bottom, #f97316, #fb923c, #fdba74);
          border-radius: 2px;
        }
        .tp-tl-item {
          background: #fff; padding: 20px 24px; border-radius: 12px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04); margin-bottom: 14px;
          border: 1px solid rgba(0,0,0,0.06);
          position: relative; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          opacity: 0; animation: slideInRight 0.5s ease forwards;
        }
        .tp-tl-item:hover { box-shadow: 0 6px 24px rgba(0,0,0,0.08); transform: translateX(4px); border-color: rgba(249,115,22,0.1); }
        .tp-tl-item.latest {
          background: linear-gradient(135deg, #fff7ed, #ffedd5);
          border: 1px solid rgba(249,115,22,0.15);
        }
        .tp-tl-dot {
          position: absolute; left: -28px; top: 22px;
          width: 14px; height: 14px; border-radius: 50%;
          background: #f97316; border: 3px solid #fff;
          box-shadow: 0 0 0 3px rgba(249,115,22,0.15);
        }
        .tp-tl-dot.first {
          width: 20px; height: 20px; left: -31px; top: 20px;
          animation: pulse 2s infinite;
          background: linear-gradient(135deg, #f97316, #ea580c);
          box-shadow: 0 0 0 4px rgba(249,115,22,0.2), 0 0 12px rgba(249,115,22,0.15);
        }
        .tp-tl-dot.past { background: #cbd5e1; box-shadow: 0 0 0 3px rgba(203,213,225,0.2); }
        .tp-tl-row {
          display: flex; justify-content: space-between; align-items: flex-start;
          flex-wrap: wrap; gap: 10px;
        }
        .tp-tl-row h4 {
          font-weight: 700; font-size: 14px; color: #1e293b;
          display: flex; align-items: center; gap: 8px; flex: 1;
        }
        .tp-tl-row h4 i { color: #f97316; font-size: 13px; flex-shrink: 0; }
        .tp-tl-date {
          font-size: 11px; color: #64748b; background: #f1f5f9; padding: 5px 12px;
          border-radius: 6px; white-space: nowrap; display: flex; align-items: center; gap: 5px;
        }
        .tp-tl-date i { font-size: 9px; }
        .tp-tl-desc {
          font-size: 13px; margin-top: 10px; color: #64748b; line-height: 1.6;
          padding-left: 20px; border-left: 2px solid #e2e8f0;
        }
        .tp-tl-notif {
          font-size: 11px; margin-top: 8px; color: #94a3b8;
          padding-left: 20px; font-style: italic;
        }

        .tp-error-card {
          background: linear-gradient(135deg, #fff5f5, #fed7d7); padding: 35px;
          border-radius: 18px; text-align: center; border: 1px solid #feb2b2;
        }
        .tp-error-icon {
          width: 60px; height: 60px; border-radius: 50%; margin: 0 auto 15px;
          background: rgba(229,62,62,0.1); display: flex; align-items: center; justify-content: center;
        }
        .tp-error-icon i { font-size: 24px; color: #e53e3e; }
        .tp-error-card h4 { color: #c53030; font-size: 18px; font-weight: 700; margin-bottom: 6px; }
        .tp-error-card p { color: #e53e3e; font-size: 14px; font-weight: 400; }

        .tp-empty-card {
          background: #fff; padding: 40px; border-radius: 18px; text-align: center;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06); margin-top: 20px;
          border: 1px solid rgba(0,0,0,0.04);
        }
        .tp-empty-icon {
          width: 70px; height: 70px; border-radius: 50%; margin: 0 auto 15px;
          background: #f5f5f5; display: flex; align-items: center; justify-content: center;
        }
        .tp-empty-icon i { font-size: 28px; color: #ccc; }

        /* ===== ABOUT ===== */
        .tp-about { padding: 100px 5%; background: #fff; }
        .tp-about-grid {
          display: flex; gap: 60px; max-width: 1140px; margin: 0 auto;
          align-items: center; flex-wrap: wrap;
        }
        .tp-about-img { flex: 1; min-width: 320px; position: relative; }
        .tp-about-img img { width: 100%; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); }
        .tp-about-img::after {
          content: ''; position: absolute; bottom: -15px; right: -15px;
          width: 120px; height: 120px; background: #f90; border-radius: 12px;
          z-index: -1; opacity: 0.2;
        }
        .tp-about-text { flex: 1; min-width: 320px; }
        .tp-label {
          color: #f90; font-weight: 600; text-transform: uppercase;
          font-size: 13px; letter-spacing: 2px;
          display: inline-block; background: #fff7ed;
          padding: 6px 14px; border-radius: 5px;
        }
        .tp-title {
          font-size: 42px; font-weight: 700; color: #f90;
          line-height: 1.15; margin-top: 15px; text-transform: capitalize;
        }
        .tp-desc { color: #666; font-size: 17px; line-height: 1.7; margin-top: 18px; }
        .tp-stats { display: flex; gap: 35px; margin-top: 35px; }
        .tp-stat {
          background: linear-gradient(135deg, #fff7ed, #ffedd5);
          padding: 20px 28px; border-radius: 12px;
          border: 1px solid rgba(255,153,0,0.15);
        }
        .tp-stat h4 { font-size: 30px; color: #f90; font-weight: 800; }
        .tp-stat p { color: #888; font-size: 13px; margin-top: 4px; font-weight: 500; }

        /* ===== SERVICES ===== */
        .tp-services { padding: 100px 5%; background: #fafafa; }
        .tp-services-head { text-align: center; margin-bottom: 50px; }
        .tp-svc-grid {
          display: flex; gap: 30px; max-width: 1140px; margin: 0 auto;
          flex-wrap: wrap; justify-content: center;
        }
        .tp-svc-box {
          flex: 1; min-width: 300px; max-width: 360px;
          background: #fff; border-radius: 16px; padding: 45px 35px;
          box-shadow: 0 5px 25px rgba(0,0,0,0.05); text-align: center;
          transition: all 0.4s cubic-bezier(0.4,0,0.2,1);
          border: 1px solid rgba(0,0,0,0.04); position: relative; overflow: hidden;
        }
        .tp-svc-box::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0;
          height: 4px; background: linear-gradient(90deg, #f90, #ffcc80);
          transform: scaleX(0); transition: transform 0.4s; transform-origin: left;
        }
        .tp-svc-box:hover { transform: translateY(-10px); box-shadow: 0 15px 40px rgba(0,0,0,0.1); }
        .tp-svc-box:hover::before { transform: scaleX(1); }
        .tp-svc-icon-wrap {
          width: 80px; height: 80px; margin: 0 auto 25px;
          background: linear-gradient(135deg, #fff7ed, #ffedd5);
          border-radius: 20px; display: flex; align-items: center; justify-content: center;
          transition: all 0.4s;
        }
        .tp-svc-box:hover .tp-svc-icon-wrap { background: #f90; transform: rotate(5deg) scale(1.05); }
        .tp-svc-icon-wrap i { font-size: 32px; color: #f90; transition: color 0.3s; }
        .tp-svc-box:hover .tp-svc-icon-wrap i { color: #fff; }
        .tp-svc-box h3 { font-size: 19px; font-weight: 700; color: #222; margin-bottom: 14px; }
        .tp-svc-box p { color: #777; font-size: 15px; line-height: 1.7; }

        /* ===== TRUST BANNER ===== */
        .tp-trust {
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 50px 5%; text-align: center;
        }
        .tp-trust-grid {
          display: flex; gap: 40px; max-width: 900px; margin: 0 auto;
          justify-content: center; flex-wrap: wrap;
        }
        .tp-trust-item { display: flex; align-items: center; gap: 14px; }
        .tp-trust-icon {
          width: 50px; height: 50px; border-radius: 14px;
          background: rgba(255,153,0,0.15); display: flex;
          align-items: center; justify-content: center;
        }
        .tp-trust-icon i { font-size: 20px; color: #f90; }
        .tp-trust-text h5 { color: #fff; font-size: 16px; font-weight: 600; }
        .tp-trust-text p { color: #8892b0; font-size: 13px; margin-top: 2px; }

        /* ===== CONTACT ===== */
        .tp-contact { padding: 100px 5%; background: #fff; }
        .tp-contact-head { text-align: center; margin-bottom: 45px; }
        .tp-contact-form { max-width: 700px; margin: 0 auto; }
        .tp-form-row { display: flex; gap: 20px; margin-bottom: 20px; }
        .tp-contact-form input, .tp-contact-form textarea {
          width: 100%; padding: 16px 22px; border: 2px solid #eee;
          border-radius: 10px; font-size: 16px; font-family: 'Lexend', sans-serif;
          outline: none; transition: all 0.3s; color: #444; background: #fafafa;
        }
        .tp-contact-form input:focus, .tp-contact-form textarea:focus {
          border-color: #f90; background: #fff; box-shadow: 0 0 0 4px rgba(255,153,0,0.1);
        }
        .tp-contact-form textarea { min-height: 140px; resize: vertical; margin-bottom: 20px; }
        .tp-btn {
          padding: 16px 50px; background: linear-gradient(135deg, #f90, #e08800); color: #fff;
          border: none; border-radius: 10px; cursor: pointer; font-weight: 700; font-size: 16px;
          font-family: 'Lexend', sans-serif; transition: all 0.3s; letter-spacing: 0.5px;
          box-shadow: 0 4px 15px rgba(255,153,0,0.35);
        }
        .tp-btn:hover { transform: translateY(-3px); box-shadow: 0 8px 25px rgba(255,153,0,0.4); }

        /* ===== FOOTER ===== */
        .tp-footer {
          background: linear-gradient(135deg, #0a0a14, #1a1a2e); color: #666;
          padding: 40px 5%; text-align: center;
        }
        .tp-footer-top {
          display: flex; justify-content: space-between; align-items: center;
          max-width: 1140px; margin: 0 auto; padding-bottom: 25px;
          border-bottom: 1px solid rgba(255,255,255,0.06); flex-wrap: wrap; gap: 20px;
        }
        .tp-footer-logo img { height: 35px; opacity: 0.8; }
        .tp-footer-links { display: flex; gap: 25px; }
        .tp-footer-links a { color: #888; font-size: 14px; transition: color 0.3s; cursor: pointer; }
        .tp-footer-links a:hover { color: #f90; }
        .tp-footer-copy { margin-top: 25px; font-size: 14px; }

        /* ===== BACK TO TOP ===== */
        .tp-btt {
          position: fixed; bottom: 30px; right: 30px; width: 48px; height: 48px;
          background: linear-gradient(135deg, #f90, #e08800); color: #fff;
          border: none; border-radius: 14px; font-size: 18px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 5px 20px rgba(255,153,0,0.35);
          transition: all 0.4s cubic-bezier(0.4,0,0.2,1);
          opacity: ${scrolled ? '1' : '0'}; pointer-events: ${scrolled ? 'auto' : 'none'};
          transform: ${scrolled ? 'translateY(0)' : 'translateY(20px)'};
        }
        .tp-btt:hover { transform: translateY(-4px); box-shadow: 0 8px 30px rgba(255,153,0,0.45); }

        /* ===== HAMBURGER ===== */
        .tp-hamburger { display: none; flex-direction: column; gap: 5px; cursor: pointer; padding: 5px; }
        .tp-hamburger span {
          width: 24px; height: 2.5px; background: #f90; border-radius: 2px;
          transition: all 0.3s;
        }
        .tp-mobile-menu {
          display: none; position: fixed; top: 75px; left: 0; right: 0;
          background: rgba(10,10,20,0.97); backdrop-filter: blur(12px);
          padding: 20px; z-index: 1023;
        }
        .tp-mobile-menu.active { display: block; animation: fadeIn 0.3s ease; }
        .tp-mobile-menu a {
          display: block; color: #f90; font-size: 18px; font-weight: 500;
          padding: 15px 10px; border-bottom: 1px solid rgba(255,255,255,0.05);
          cursor: pointer; transition: color 0.3s;
        }
        .tp-mobile-menu a:hover { color: #fff; }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 900px) {
          .tp-header { padding: 0 20px; }
          .tp-nav { display: none; }
          .tp-hamburger { display: flex; }
          .tp-hero { min-height: 450px; padding: 0 20px; }
          .tp-hero h1 { font-size: 30px; }
          .tp-hero .tp-sub { font-size: 15px; }
          .tp-hero-badges { gap: 12px; }
          .tp-badge { padding: 8px 14px; }
          .tp-search { padding: 20px; }
          .tp-search h4 { width: 100%; text-align: center; }
          .tp-search form { flex-direction: column; width: 100%; }
          .tp-search input { min-width: auto; }
          .tp-about, .tp-services, .tp-contact { padding: 60px 20px; }
          .tp-about-grid { flex-direction: column; gap: 30px; }
          .tp-svc-grid { flex-direction: column; align-items: center; }
          .tp-form-row { flex-direction: column; }
          .tp-title { font-size: 28px; }
          .tp-trust-grid { gap: 25px; }
          .tp-footer-top { flex-direction: column; text-align: center; }
          .tp-timeline { padding-left: 28px; }
          .tp-route-cards { grid-template-columns: 1fr; }
          .tp-status-code-val { font-size: 20px; }
          .tp-status-header { padding: 25px 20px 20px; }
        }

        @media (max-width: 768px) {
          .tp-hero { min-height: 400px; padding: 0 16px; }
          .tp-hero h1 { font-size: 26px; max-width: 100%; }
          .tp-hero .tp-sub { font-size: 14px; max-width: 100%; margin-top: 14px; }
          .tp-hero-badges { flex-direction: column; gap: 10px; margin-top: 25px; }
          .tp-badge { padding: 8px 16px; }
          .tp-badge span { font-size: 12px; }
          .tp-search { padding: 18px 16px; gap: 14px; }
          .tp-search h4 { font-size: 15px; }
          .tp-search input { padding: 14px 16px; font-size: 14px; }
          .tp-search button { padding: 14px 24px; font-size: 14px; }
          .tp-results { padding: 30px 16px 40px; }
          .tp-status-header { padding: 22px 18px 18px; margin-bottom: 18px; }
          .tp-status-code { font-size: 10px; }
          .tp-status-code-val { font-size: 18px; letter-spacing: 2px; }
          .tp-status-badge { padding: 8px 14px; font-size: 12px; }
          .tp-progress-labels { font-size: 10px; }
          .tp-route-card { padding: 16px; gap: 12px; }
          .tp-route-icon { width: 42px; height: 42px; }
          .tp-route-icon i { font-size: 16px !important; }
          .tp-route-label { font-size: 10px; }
          .tp-route-value { font-size: 13px; }
          .tp-steps-title { font-size: 16px; }
          .tp-steps-count { font-size: 11px; padding: 5px 12px; }
          .tp-timeline { padding-left: 24px; }
          .tp-tl-item { padding: 16px 18px; margin-bottom: 12px; }
          .tp-tl-row h4 { font-size: 13px; }
          .tp-tl-date { font-size: 10px; padding: 4px 10px; }
          .tp-tl-desc { font-size: 12px; margin-top: 8px; padding-left: 14px; }
          .tp-tl-notif { font-size: 10px; padding-left: 14px; }
          .tp-tl-dot { left: -26px; width: 12px; height: 12px; }
          .tp-tl-dot.first { width: 18px; height: 18px; left: -29px; }
          .tp-error-card { padding: 28px 20px; }
          .tp-error-card h4 { font-size: 16px; }
          .tp-error-card p { font-size: 13px; }
          .tp-empty-card { padding: 30px 20px; }
          .tp-about, .tp-services, .tp-contact { padding: 50px 16px; }
          .tp-about-img { min-width: auto; }
          .tp-about-text { min-width: auto; }
          .tp-title { font-size: 24px; }
          .tp-desc { font-size: 15px; }
          .tp-stats { gap: 20px; flex-wrap: wrap; }
          .tp-stat { padding: 16px 20px; }
          .tp-stat h4 { font-size: 24px; }
          .tp-stat p { font-size: 12px; }
          .tp-svc-box { min-width: auto; max-width: 100%; padding: 35px 25px; }
          .tp-svc-box h3 { font-size: 17px; }
          .tp-svc-box p { font-size: 14px; }
          .tp-svc-icon-wrap { width: 65px; height: 65px; }
          .tp-svc-icon-wrap i { font-size: 26px; }
          .tp-trust { padding: 40px 16px; }
          .tp-trust-grid { gap: 20px; flex-direction: column; align-items: center; }
          .tp-trust-text h5 { font-size: 14px; }
          .tp-trust-text p { font-size: 12px; }
          .tp-contact-form input, .tp-contact-form textarea { padding: 14px 16px; font-size: 14px; }
          .tp-btn { padding: 14px 40px; font-size: 15px; }
          .tp-footer { padding: 30px 16px; }
          .tp-footer-links { gap: 18px; flex-wrap: wrap; justify-content: center; }
          .tp-footer-links a { font-size: 13px; }
          .tp-footer-copy { font-size: 12px; }
          .tp-btt { bottom: 20px; right: 20px; width: 42px; height: 42px; font-size: 16px; border-radius: 12px; }
        }

        @media (max-width: 480px) {
          .tp-header { height: 65px; padding: 0 14px; }
          .tp-header-logo img { height: 36px; }
          .tp-mobile-menu { top: 65px; padding: 16px; }
          .tp-mobile-menu a { font-size: 16px; padding: 12px 8px; }
          .tp-hero { min-height: 360px; padding: 0 14px; }
          .tp-hero h1 { font-size: 22px; }
          .tp-hero .tp-sub { font-size: 13px; margin-top: 12px; line-height: 1.5; }
          .tp-hero-badges { margin-top: 20px; }
          .tp-badge { padding: 7px 12px; border-radius: 30px; }
          .tp-badge i { font-size: 13px; }
          .tp-badge span { font-size: 11px; }
          .tp-search { padding: 16px 14px; gap: 12px; }
          .tp-search h4 { font-size: 14px; }
          .tp-search input { padding: 12px 14px; font-size: 13px; border-radius: 6px; }
          .tp-search button { padding: 12px 20px; font-size: 13px; border-radius: 6px; justify-content: center; }
          .tp-results { padding: 24px 12px 30px; }
          .tp-status-header { padding: 18px 14px 16px; border-radius: 12px; margin-bottom: 16px; }
          .tp-status-top { gap: 12px; }
          .tp-status-code-val { font-size: 16px; letter-spacing: 1.5px; }
          .tp-status-badge { padding: 6px 12px; font-size: 11px; }
          .tp-progress-labels { font-size: 9px; }
          .tp-route-cards { gap: 12px; margin-bottom: 20px; }
          .tp-route-card { padding: 14px; gap: 10px; border-radius: 10px; }
          .tp-route-icon { width: 38px; height: 38px; border-radius: 8px; }
          .tp-route-icon i { font-size: 14px !important; }
          .tp-route-label { font-size: 9px; }
          .tp-route-value { font-size: 12px; }
          .tp-steps-header { margin-bottom: 18px; }
          .tp-steps-title { font-size: 15px; gap: 8px; }
          .tp-steps-title i { font-size: 14px; }
          .tp-steps-count { font-size: 10px; padding: 4px 10px; }
          .tp-timeline { padding-left: 22px; }
          .tp-timeline::before { left: 10px; }
          .tp-tl-item { padding: 14px 14px; margin-bottom: 10px; border-radius: 10px; }
          .tp-tl-row { gap: 8px; }
          .tp-tl-row h4 { font-size: 12px; gap: 6px; }
          .tp-tl-row h4 i { font-size: 11px; }
          .tp-tl-date { font-size: 9px; padding: 3px 8px; border-radius: 4px; }
          .tp-tl-desc { font-size: 11px; padding-left: 12px; margin-top: 6px; line-height: 1.5; }
          .tp-tl-notif { font-size: 9px; padding-left: 12px; margin-top: 6px; }
          .tp-tl-dot { left: -24px; width: 10px; height: 10px; border-width: 2px; }
          .tp-tl-dot.first { width: 16px; height: 16px; left: -27px; }
          .tp-error-card { padding: 24px 16px; border-radius: 14px; }
          .tp-error-icon { width: 50px; height: 50px; }
          .tp-error-icon i { font-size: 20px; }
          .tp-error-card h4 { font-size: 15px; }
          .tp-error-card p { font-size: 12px; }
          .tp-empty-card { padding: 25px 16px; border-radius: 14px; }
          .tp-empty-icon { width: 55px; height: 55px; }
          .tp-empty-icon i { font-size: 22px; }
          .tp-about, .tp-services, .tp-contact { padding: 40px 14px; }
          .tp-label { font-size: 11px; letter-spacing: 1.5px; padding: 5px 10px; }
          .tp-title { font-size: 22px; margin-top: 12px; }
          .tp-desc { font-size: 14px; line-height: 1.6; margin-top: 14px; }
          .tp-about-img img { border-radius: 10px; }
          .tp-about-img::after { width: 80px; height: 80px; bottom: -10px; right: -10px; }
          .tp-stats { gap: 14px; }
          .tp-stat { padding: 14px 18px; border-radius: 10px; flex: 1; min-width: 0; }
          .tp-stat h4 { font-size: 20px; }
          .tp-stat p { font-size: 11px; }
          .tp-svc-box { padding: 30px 20px; border-radius: 14px; }
          .tp-svc-box h3 { font-size: 16px; margin-bottom: 10px; }
          .tp-svc-box p { font-size: 13px; line-height: 1.6; }
          .tp-svc-icon-wrap { width: 58px; height: 58px; margin-bottom: 20px; border-radius: 16px; }
          .tp-svc-icon-wrap i { font-size: 24px; }
          .tp-trust { padding: 35px 14px; }
          .tp-trust-icon { width: 42px; height: 42px; border-radius: 10px; }
          .tp-trust-icon i { font-size: 17px; }
          .tp-trust-text h5 { font-size: 13px; }
          .tp-trust-text p { font-size: 11px; }
          .tp-contact-head { margin-bottom: 30px; }
          .tp-contact-form input, .tp-contact-form textarea { padding: 12px 14px; font-size: 13px; border-radius: 8px; }
          .tp-contact-form textarea { min-height: 110px; }
          .tp-btn { padding: 13px 30px; font-size: 14px; border-radius: 8px; width: 100%; }
          .tp-footer { padding: 25px 14px; }
          .tp-footer-logo img { height: 30px; }
          .tp-footer-links { gap: 14px; }
          .tp-footer-links a { font-size: 12px; }
          .tp-footer-copy { font-size: 11px; margin-top: 18px; }
          .tp-btt { bottom: 16px; right: 16px; width: 38px; height: 38px; font-size: 14px; border-radius: 10px; }
        }

        @media (max-width: 360px) {
          .tp-hero { min-height: 320px; padding: 0 12px; }
          .tp-hero h1 { font-size: 20px; }
          .tp-hero .tp-sub { font-size: 12px; }
          .tp-badge span { font-size: 10px; }
          .tp-search h4 { font-size: 13px; }
          .tp-search input { padding: 11px 12px; font-size: 12px; }
          .tp-search button { padding: 11px 16px; font-size: 12px; }
          .tp-status-code-val { font-size: 14px; letter-spacing: 1px; }
          .tp-status-badge { font-size: 10px; padding: 5px 10px; }
          .tp-route-card { padding: 12px; }
          .tp-route-icon { width: 34px; height: 34px; }
          .tp-route-value { font-size: 11px; }
          .tp-tl-item { padding: 12px 12px; }
          .tp-tl-row h4 { font-size: 11px; }
          .tp-tl-desc { font-size: 10px; }
          .tp-title { font-size: 20px; }
          .tp-desc { font-size: 13px; }
          .tp-stat h4 { font-size: 18px; }
          .tp-svc-box { padding: 25px 16px; }
          .tp-svc-box h3 { font-size: 15px; }
          .tp-svc-box p { font-size: 12px; }
        }
      `}</style>

      <div className="tp">
        {/* Header */}
        <header className="tp-header">
          <ul className="tp-nav">
            <li><a onClick={() => scrollTo('home')}>Início</a></li>
            <li><a onClick={() => scrollTo('about')}>Sobre Nós</a></li>
            <li><a onClick={() => scrollTo('service')}>Serviços</a></li>
            <li><a onClick={() => scrollTo('contact')}>Contato</a></li>
          </ul>
          <div className="tp-hamburger" onClick={() => setMobileMenu(!mobileMenu)}>
            <span></span><span></span><span></span>
          </div>
        </header>

        {/* Mobile Menu */}
        <div className={`tp-mobile-menu ${mobileMenu ? 'active' : ''}`}>
          <a onClick={() => scrollTo('home')}>Início</a>
          <a onClick={() => scrollTo('about')}>Sobre Nós</a>
          <a onClick={() => scrollTo('service')}>Serviços</a>
          <a onClick={() => scrollTo('contact')}>Contato</a>
        </div>

        {/* Hero */}
        <div className="tp-hero" id="home">
          <div className="tp-hero-content">
            <h1>Rastreie suas entregas de forma rápida e segura.</h1>
            <p className="tp-sub">Especialistas em soluções logísticas rápidas, eficientes e seguras para conectar pessoas e empresas em todo o mundo.</p>
            <div className="tp-hero-badges">
              <div className="tp-badge"><i className="fas fa-shield-alt"></i><span>Rastreio Seguro</span></div>
              <div className="tp-badge"><i className="fas fa-clock"></i><span>Atualizações em Tempo Real</span></div>
              <div className="tp-badge"><i className="fas fa-globe"></i><span>Cobertura Mundial</span></div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="tp-search">
          <h4>Digite seu código de rastreio</h4>
          <form onSubmit={handleSearch}>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Digite seu código de rastreio"
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? <div className="tp-spinner"></div> : <i className="fas fa-search"></i>}
              {loading ? 'Buscando...' : 'Rastrear'}
            </button>
          </form>
        </div>

        {/* Tracking Results */}
        <div ref={resultsRef}>
          {(searched || trackingInfo || error) && (
            <div className="tp-results">
              {error && (
                <div className="tp-error-card">
                  <div className="tp-error-icon"><i className="fas fa-exclamation-triangle"></i></div>
                  <h4>Rastreio Não Encontrado</h4>
                  <p>{error}</p>
                </div>
              )}

              {trackingInfo && (() => {
                const totalEtapas = trackingInfo.etapas ? trackingInfo.etapas.length : 0;
                const isDelivered = trackingInfo.status && trackingInfo.status.toLowerCase().includes('entregue');
                const progressPercent = totalEtapas > 0 ? (isDelivered ? 100 : Math.min(95, Math.round((totalEtapas / (totalEtapas + 2)) * 100))) : 5;
                return (
                  <>
                    {/* Status Header */}
                    <div className="tp-status-header">
                      <div className="tp-status-top">
                        <div>
                          <div className="tp-status-code">Código de Rastreio</div>
                          <div className="tp-status-code-val">{trackingInfo.codigo}</div>
                        </div>
                        <div className={`tp-status-badge ${isDelivered ? 'delivered' : ''}`}>
                          <i className="fas fa-circle"></i>
                          {trackingInfo.status || 'Em processamento'}
                        </div>
                      </div>
                      <div className="tp-progress">
                        <div className="tp-progress-bar">
                          <div className="tp-progress-fill" style={{ width: `${progressPercent}%` }}></div>
                        </div>
                        <div className="tp-progress-labels">
                          <span>Despachado</span>
                          <span>Em Trânsito</span>
                          <span>Saiu para Entrega</span>
                          <span>{isDelivered ? 'Entregue' : 'Entrega'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Route Info Cards */}
                    <div className="tp-route-cards">
                      {trackingInfo.origem && trackingInfo.origem !== 'Não especificado' && (
                        <div className="tp-route-card">
                          <div className="tp-route-icon origin"><i className="fas fa-paper-plane"></i></div>
                          <div>
                            <div className="tp-route-label">Origem</div>
                            <div className="tp-route-value">{trackingInfo.origem}</div>
                          </div>
                        </div>
                      )}
                      {trackingInfo.destino && trackingInfo.destino !== 'Não especificado' && (
                        <div className="tp-route-card">
                          <div className="tp-route-icon dest"><i className="fas fa-map-marker-alt"></i></div>
                          <div>
                            <div className="tp-route-label">Destino</div>
                            <div className="tp-route-value">{trackingInfo.destino}</div>
                          </div>
                        </div>
                      )}
                      {trackingInfo.data_criacao && (
                        <div className="tp-route-card">
                          <div className="tp-route-icon date"><i className="fas fa-calendar-alt"></i></div>
                          <div>
                            <div className="tp-route-label">Criado em</div>
                            <div className="tp-route-value">{(() => { try { const d = new Date(trackingInfo.data_criacao.replace(' ', 'T')); return isNaN(d) ? trackingInfo.data_criacao : d.toLocaleDateString('pt-BR'); } catch(e) { return trackingInfo.data_criacao; } })()}</div>
                          </div>
                        </div>
                      )}
                      <div className="tp-route-card">
                        <div className="tp-route-icon status"><i className="fas fa-route"></i></div>
                        <div>
                          <div className="tp-route-label">Total de Atualizações</div>
                          <div className="tp-route-value">{totalEtapas} etapas rastreadas</div>
                        </div>
                      </div>
                    </div>

                    {/* Timeline */}
                    {totalEtapas > 0 ? (
                      <>
                        <div className="tp-steps-header">
                          <div className="tp-steps-title"><i className="fas fa-history"></i> Histórico de Rastreamento</div>
                          <div className="tp-steps-count">{totalEtapas} atualizações</div>
                        </div>
                        <div className="tp-timeline">
                          {trackingInfo.etapas.map((etapa, index) => (
                            <div key={index} className={`tp-tl-item ${index === 0 ? 'latest' : ''}`} style={{ animationDelay: `${index * 0.08}s` }}>
                              <div className={`tp-tl-dot ${index === 0 ? 'first' : index > 3 ? 'past' : ''}`}></div>
                              <div className="tp-tl-row">
                                <h4><i className={`fas ${index === 0 ? 'fa-star' : 'fa-box'}`}></i>{etapa.local || 'Local não especificado'}</h4>
                                <span className="tp-tl-date"><i className="far fa-clock"></i>{(() => {
                                  try {
                                    const d = new Date(String(etapa.data).replace(' ', 'T'));
                                    if (isNaN(d)) return etapa.data;
                                    const dd = String(d.getDate()).padStart(2, '0');
                                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                                    const yyyy = d.getFullYear();
                                    const hh = String(d.getHours()).padStart(2, '0');
                                    const mi = String(d.getMinutes()).padStart(2, '0');
                                    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
                                  } catch (_) { return etapa.data; }
                                })()}</span>
                              </div>
                              <p className="tp-tl-desc">{etapa.descricao || 'Sem descrição'}</p>
                              {etapa.notificacao && <p className="tp-tl-notif"><i className="fas fa-bell" style={{ marginRight: '5px', fontSize: '10px' }}></i>{etapa.notificacao}</p>}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="tp-empty-card">
                        <div className="tp-empty-icon"><i className="fas fa-inbox"></i></div>
                        <h4 style={{ color: '#555', marginBottom: '5px' }}>Sem Atualizações Ainda</h4>
                        <p style={{ color: '#999', fontSize: '14px' }}>As informações de rastreamento aparecerão aqui assim que o envio for processado.</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* About Section */}
        <div className="tp-about" id="about">
          <div className="tp-about-grid">
            <div className="tp-about-img">
              <img src="/images/about.jpg" alt="Sobre a COMEX Tracking" />
            </div>
            <div className="tp-about-text">
              <p className="tp-label">Sobre Nós</p>
              <h2 className="tp-title">Líderes em logística e transporte.</h2>
              <p className="tp-desc">A Comex Tracking oferece excelência em transporte, garantindo que seus pedidos sempre cheguem no prazo e em perfeitas condições.</p>
              <div className="tp-stats">
                <div className="tp-stat">
                  <h4>20.000+</h4>
                  <p>Pontos Logísticos</p>
                </div>
                <div className="tp-stat">
                  <h4>10M+</h4>
                  <p>Entregas Realizadas</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="tp-services" id="service">
          <div className="tp-services-head">
            <p className="tp-label">Nossos Serviços.</p>
            <h2 className="tp-title">Soluções logísticas completas</h2>
          </div>
          <div className="tp-svc-grid">
            <div className="tp-svc-box">
              <div className="tp-svc-icon-wrap"><i className="fas fa-truck"></i></div>
              <h3>Cobertura Nacional.</h3>
              <p>Atendemos todo o Brasil com um sistema de transporte rápido e eficiente, garantindo pontualidade em cada entrega.</p>
            </div>
            <div className="tp-svc-box">
              <div className="tp-svc-icon-wrap"><i className="fas fa-plane-departure"></i></div>
              <h3>Envio Expresso.</h3>
              <p>Perfeito para demandas urgentes, o envio expresso garante que seu pedido chegue ao destino com máxima velocidade.</p>
            </div>
            <div className="tp-svc-box">
              <div className="tp-svc-icon-wrap"><i className="fas fa-shopping-bag"></i></div>
              <h3>Proteção de Itens.</h3>
              <p>Seus itens são transportados com segurança graças às nossas técnicas avançadas de embalagem e proteção.</p>
            </div>
          </div>
        </div>

        {/* Trust Banner */}
        <div className="tp-trust">
          <div className="tp-trust-grid">
            <div className="tp-trust-item">
              <div className="tp-trust-icon"><i className="fas fa-shield-alt"></i></div>
              <div className="tp-trust-text">
                <h5>100% Seguro</h5>
                <p>Criptografia de ponta a ponta</p>
              </div>
            </div>
            <div className="tp-trust-item">
              <div className="tp-trust-icon"><i className="fas fa-headset"></i></div>
              <div className="tp-trust-text">
                <h5>Suporte 24/7</h5>
                <p>Sempre aqui para você</p>
              </div>
            </div>
            <div className="tp-trust-item">
              <div className="tp-trust-icon"><i className="fas fa-bolt"></i></div>
              <div className="tp-trust-text">
                <h5>Atualizações Rápidas</h5>
                <p>Rastreamento em tempo real</p>
              </div>
            </div>
            <div className="tp-trust-item">
              <div className="tp-trust-icon"><i className="fas fa-globe-americas"></i></div>
              <div className="tp-trust-text">
                <h5>Rede Global</h5>
                <p>Mais de 200 países atendidos</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="tp-contact" id="contact">
          <div className="tp-contact-head">
            <h2 className="tp-title">Entre em contato</h2>
            <p className="tp-desc">Dúvidas ou sugestões? Preencha o formulário abaixo e nossa equipe entrará em contato com você.</p>
          </div>
          <div className="tp-contact-form">
            <div className="tp-form-row">
              <input type="text" placeholder="Seu nome" />
              <input type="email" placeholder="Seu e-mail" />
            </div>
            <textarea placeholder="Sua mensagem"></textarea>
            <div style={{ textAlign: 'center' }}>
              <button className="tp-btn" type="button">Enviar Mensagem</button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="tp-footer">
          <div className="tp-footer-top">
            <div className="tp-footer-links">
              <a onClick={() => scrollTo('home')}>Início</a>
              <a onClick={() => scrollTo('about')}>Sobre Nós</a>
              <a onClick={() => scrollTo('service')}>Serviços</a>
              <a onClick={() => scrollTo('contact')}>Contato</a>
            </div>
          </div>
          <p className="tp-footer-copy">Copyright &copy; 2026 Comex Tracking. Todos os direitos reservados.</p>
        </div>

        {/* Back to Top */}
        <button className="tp-btt" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <i className="fas fa-angle-up"></i>
        </button>
      </div>
    </>
  );
};

export default TrackOrderPT;
